import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { PredictionMetadata } from '../../../types/models/prediction'
import { ProjectAnnotationType } from '../../../types/models/project'

export interface TagLite {
  id: string
  name: string
}

export interface NormalizedBox {
  points: number[]
  xmin: number
  ymin: number
  xmax: number
  ymax: number
}

/**
 * Normalize a box-like structure to pixel-space points and min/max.
 * Accepts either prediction bbox objects (points and/or min/max in 0–1) or
 * UI boxes that may be missing some derived fields.
 */
export function normalizeBoxToPixels(
  box:
    | { points?: number[]; xmin?: number; ymin?: number; xmax?: number; ymax?: number }
    | AnnotationDataBoundingBox,
  imageWidth: number,
  imageHeight: number
): NormalizedBox | null {
  if (!box) return null

  // Case 1: points exist
  if (Array.isArray((box as any).points) && (box as any).points.length === 8) {
    const pts = (box as any).points as number[]
    const maxX = Math.max(pts[0], pts[2], pts[4], pts[6])
    const maxY = Math.max(pts[1], pts[3], pts[5], pts[7])

    const pixelPoints =
      maxX <= 1 && maxY <= 1
        ? [
            pts[0] * imageWidth,
            pts[1] * imageHeight,
            pts[2] * imageWidth,
            pts[3] * imageHeight,
            pts[4] * imageWidth,
            pts[5] * imageHeight,
            pts[6] * imageWidth,
            pts[7] * imageHeight,
          ]
        : [...pts]

    const xVals = [pixelPoints[0], pixelPoints[2], pixelPoints[4], pixelPoints[6]]
    const yVals = [pixelPoints[1], pixelPoints[3], pixelPoints[5], pixelPoints[7]]

    return {
      points: pixelPoints,
      xmin: Math.min(...xVals),
      ymin: Math.min(...yVals),
      xmax: Math.max(...xVals),
      ymax: Math.max(...yVals),
    }
  }

  // Case 2: min/max exist
  const hasMinMax =
    (box as any).xmin !== undefined &&
    (box as any).xmax !== undefined &&
    (box as any).ymin !== undefined &&
    (box as any).ymax !== undefined

  if (hasMinMax) {
    const xminRaw = (box as any).xmin as number
    const yminRaw = (box as any).ymin as number
    const xmaxRaw = (box as any).xmax as number
    const ymaxRaw = (box as any).ymax as number

    const normalized = xmaxRaw <= 1 && ymaxRaw <= 1
    const xmin = normalized ? xminRaw * imageWidth : xminRaw
    const ymin = normalized ? yminRaw * imageHeight : yminRaw
    const xmax = normalized ? xmaxRaw * imageWidth : xmaxRaw
    const ymax = normalized ? ymaxRaw * imageHeight : ymaxRaw

    const points = [xmin, ymin, xmax, ymin, xmax, ymax, xmin, ymax]

    return { points, xmin, ymin, xmax, ymax }
  }

  return null
}

/**
 * Convert prediction metadata list to pixel-space UI boxes for the canvas.
 */
export function predictionToUIBoxes(
  predictions: PredictionMetadata[],
  tags: TagLite[],
  imageWidth: number,
  imageHeight: number,
  projectType: ProjectAnnotationType,
  startClassIndex = 0
): AnnotationDataBoundingBox[] {
  return predictions
    .map((pred, idx) => {
      const bboxLike = pred.bounding_boxes || pred.oriented_bounding_boxes
      if (!bboxLike) return null

      const normalized = normalizeBoxToPixels(bboxLike, imageWidth, imageHeight)
      if (!normalized) return null

      const tag =
        tags.find((t) => t.name === pred.class_name) || tags.find((t) => t.id === pred.tagid)

      return {
        name: tag?.id || pred.tagid || pred.class_name,
        class_name: pred.class_name,
        points: normalized.points,
        xmin: normalized.xmin,
        ymin: normalized.ymin,
        xmax: normalized.xmax,
        ymax: normalized.ymax,
        ai_labeled: true,
        confidence: pred.confidence,
        class_index: startClassIndex + idx,
      } as AnnotationDataBoundingBox
    })
    .filter((b): b is AnnotationDataBoundingBox => Boolean(b))
}

/**
 * Convert annotation metadata boxes to pixel-space UI boxes.
 * Assumes annotation boxes are already in pixel units; includes points passthrough.
 */
export function annotationToUIBoxes(
  boxes: AnnotationDataBoundingBox[] | undefined,
  projectType: ProjectAnnotationType
): AnnotationDataBoundingBox[] {
  if (!boxes || boxes.length === 0) return []
  return boxes.map((bbox, index) => ({
    name: bbox.name,
    class_name: bbox.class_name,
    points: bbox.points,
    xmin: bbox.xmin,
    ymin: bbox.ymin,
    xmax: bbox.xmax,
    ymax: bbox.ymax,
    ai_labeled: Boolean(bbox.ai_labeled),
    confidence: bbox.confidence,
    class_index: index,
  }))
}
